import { MessagingPlatform, ViewType } from '@this+that/models';
import { StateCreator } from 'zustand';

import { CacheEntry, FetchCacheStore } from './types';

const DEFAULT_CACHE_TTL_MS = 60 * 1000;
const INTEGRATIONS_CACHE_TTL_MS = 5 * 60 * 1000;
const LABELS_CACHE_TTL_MS = 3 * 60 * 1000;
const TASK_GROUPS_CACHE_TTL_MS = 2 * 60 * 1000;

export const createFetchCacheSlice: StateCreator<FetchCacheStore> = (
  set,
  get,
) => {
  return {
    loadedViewTypes: new Map<ViewType, CacheEntry>(),
    loadedSlackChannels: false,
    slackChannelsLoadedAt: null,
    loadedTeamsChats: false,
    teamsChatsLoadedAt: null,
    loadedInstagramConversations: false,
    instagramConversationsLoadedAt: null,
    loadedIntegrations: false,
    integrationsLoadedAt: null,
    loadedLabels: false,
    labelsLoadedAt: null,
    loadedTaskGroups: false,
    taskGroupsLoadedAt: null,
    taskGroupsLoadedTeamId: null,
    integrationGuards: {},

    markViewTypeAsLoaded: (viewType: ViewType) => {
      const currentLoaded = new Map(get().loadedViewTypes);
      currentLoaded.set(viewType, {
        loadedAt: Date.now(),
        viewType,
      });
      set({ loadedViewTypes: currentLoaded });
    },

    markSlackChannelsAsLoaded: () => {
      set({
        loadedSlackChannels: true,
        slackChannelsLoadedAt: Date.now(),
      });
    },

    markTeamsChatsAsLoaded: () => {
      set({
        loadedTeamsChats: true,
        teamsChatsLoadedAt: Date.now(),
      });
    },

    markInstagramConversationsAsLoaded: () => {
      set({
        loadedInstagramConversations: true,
        instagramConversationsLoadedAt: Date.now(),
      });
    },

    isViewTypeLoaded: (viewType: ViewType, maxAge = DEFAULT_CACHE_TTL_MS) => {
      const entry = get().loadedViewTypes.get(viewType);
      if (!entry) {
        return false;
      }

      const isExpired = Date.now() - entry.loadedAt > maxAge;
      if (isExpired) {
        const current = new Map(get().loadedViewTypes);
        current.delete(viewType);
        set({ loadedViewTypes: current });
        return false;
      }
      return true;
    },

    isSlackChannelsLoaded: (maxAge = DEFAULT_CACHE_TTL_MS) => {
      const { loadedSlackChannels, slackChannelsLoadedAt } = get();
      if (!loadedSlackChannels || !slackChannelsLoadedAt) {
        return false;
      }

      const isExpired = Date.now() - slackChannelsLoadedAt > maxAge;
      if (isExpired) {
        set({ loadedSlackChannels: false, slackChannelsLoadedAt: null });
        return false;
      }
      return true;
    },

    isTeamsChatsLoaded: (maxAge = DEFAULT_CACHE_TTL_MS) => {
      const { loadedTeamsChats, teamsChatsLoadedAt } = get();

      const isValid =
        loadedTeamsChats &&
        teamsChatsLoadedAt &&
        Date.now() - teamsChatsLoadedAt <= maxAge;

      if (!isValid && loadedTeamsChats) {
        set({ loadedTeamsChats: false, teamsChatsLoadedAt: null });
      }

      return Boolean(isValid);
    },

    isInstagramConversationsLoaded: (maxAge = DEFAULT_CACHE_TTL_MS) => {
      const { loadedInstagramConversations, instagramConversationsLoadedAt } =
        get();

      const isValid =
        loadedInstagramConversations &&
        instagramConversationsLoadedAt &&
        Date.now() - instagramConversationsLoadedAt <= maxAge;

      if (!isValid && loadedInstagramConversations) {
        set({
          loadedInstagramConversations: false,
          instagramConversationsLoadedAt: null,
        });
      }

      return Boolean(isValid);
    },

    markIntegrationsAsLoaded: () => {
      set({
        loadedIntegrations: true,
        integrationsLoadedAt: Date.now(),
      });
    },

    markLabelsAsLoaded: () => {
      set({
        loadedLabels: true,
        labelsLoadedAt: Date.now(),
      });
    },

    markTaskGroupsAsLoaded: (teamId: string | null) => {
      set({
        loadedTaskGroups: true,
        taskGroupsLoadedAt: Date.now(),
        taskGroupsLoadedTeamId: teamId,
      });
    },

    markIntegrationGuard: (key: MessagingPlatform, scope = null) => {
      set((state) => ({
        integrationGuards: {
          ...state.integrationGuards,
          [key]: { loadedAt: Date.now(), scope },
        },
      }));
    },

    isIntegrationGuardValid: (
      key: MessagingPlatform,
      scope = null,
      maxAge = INTEGRATIONS_CACHE_TTL_MS,
    ) => {
      const entry = get().integrationGuards[key];
      if (!entry) {
        return false;
      }
      if (entry.scope !== scope) {
        return false;
      }
      if (Date.now() - entry.loadedAt > maxAge) {
        const next = { ...get().integrationGuards };
        delete next[key];
        set({ integrationGuards: next });
        return false;
      }
      return true;
    },

    isIntegrationsLoaded: (maxAge = INTEGRATIONS_CACHE_TTL_MS) => {
      const { loadedIntegrations, integrationsLoadedAt } = get();
      if (!loadedIntegrations || !integrationsLoadedAt) {
        return false;
      }

      const isExpired = Date.now() - integrationsLoadedAt > maxAge;
      if (isExpired) {
        set({ loadedIntegrations: false, integrationsLoadedAt: null });
        return false;
      }
      return true;
    },

    isLabelsLoaded: (maxAge = LABELS_CACHE_TTL_MS) => {
      const { loadedLabels, labelsLoadedAt } = get();
      if (!loadedLabels || !labelsLoadedAt) {
        return false;
      }

      const isExpired = Date.now() - labelsLoadedAt > maxAge;
      if (isExpired) {
        set({ loadedLabels: false, labelsLoadedAt: null });
        return false;
      }
      return true;
    },

    isTaskGroupsLoaded: (teamId, maxAge = TASK_GROUPS_CACHE_TTL_MS) => {
      const { loadedTaskGroups, taskGroupsLoadedAt, taskGroupsLoadedTeamId } =
        get();
      if (!loadedTaskGroups || !taskGroupsLoadedAt) {
        return false;
      }
      // Cached list belongs to a different team (e.g. switched on another route).
      if (taskGroupsLoadedTeamId !== teamId) {
        get().invalidateTaskGroups();
        return false;
      }

      const isExpired = Date.now() - taskGroupsLoadedAt > maxAge;
      if (isExpired) {
        get().invalidateTaskGroups();
        return false;
      }
      return true;
    },

    invalidateIntegrations: () => {
      set({ loadedIntegrations: false, integrationsLoadedAt: null });
    },

    invalidateLabels: () => {
      set({ loadedLabels: false, labelsLoadedAt: null });
    },

    invalidateTeamsChats: () => {
      set({ loadedTeamsChats: false, teamsChatsLoadedAt: null });
    },

    invalidateTaskGroups: () => {
      set({
        loadedTaskGroups: false,
        taskGroupsLoadedAt: null,
        taskGroupsLoadedTeamId: null,
      });
    },

    invalidateViewType: (viewType: ViewType) => {
      const current = new Map(get().loadedViewTypes);
      current.delete(viewType);
      set({ loadedViewTypes: current });
    },

    invalidateAllViewTypes: () => {
      set({ loadedViewTypes: new Map<ViewType, CacheEntry>() });
    },

    clearCache: () => {
      set({
        loadedViewTypes: new Map<ViewType, CacheEntry>(),
        loadedSlackChannels: false,
        slackChannelsLoadedAt: null,
        loadedTeamsChats: false,
        teamsChatsLoadedAt: null,
        loadedInstagramConversations: false,
        instagramConversationsLoadedAt: null,
        loadedIntegrations: false,
        integrationsLoadedAt: null,
        loadedLabels: false,
        labelsLoadedAt: null,
        loadedTaskGroups: false,
        taskGroupsLoadedAt: null,
        taskGroupsLoadedTeamId: null,
        integrationGuards: {},
      });
    },
  };
};
